/**
 * ProductPreviewV3 - Product preview sidebar card for V3 editor
 *
 * Main 140x140 thumbnail + row of 46x46 variant thumbnails.
 */

import React, { useMemo } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '../shared/cards/Card';
import type { ProductFormStateV3, VariantV3 } from '../../types';
import type { VariantAxisConfig } from '../../../../../common/models/product-family.model';

interface ProductPreviewV3Props {
  /** Form state */
  product: ProductFormStateV3 | null;
  /** Variants */
  variants: VariantV3[];
  /** Variant axes configuration */
  variantAxes?: VariantAxisConfig[];
}

export const ProductPreviewV3: React.FC<ProductPreviewV3Props> = ({
  product,
  variants,
  variantAxes = [],
}) => {
  const { t } = useTranslation();

  // Find default variant or first selected
  const defaultVariant = useMemo(() => {
    return variants.find((v) => v.isDefault) || variants.find((v) => v.isSelected) || variants[0];
  }, [variants]);

  // Resolve preview source for a variant.
  // - If thumbnailPath is a real cropped image AND not stale, use it as-is.
  // - Otherwise fall back to materialPath with CSS zoom-crop so the preview
  //   shows a tile-sized view (matching the cropper's framing) instead of the
  //   full seamless. Saved seamlessParams.zoom drives the crop, defaulting to
  //   4 when undefined or ≤ 1 so untouched variants still render sensibly.
  //
  // "Stale" cases handled:
  //   - thumbnailPath equals materialPath (legacy: the full seamless was
  //     written to thumbnailPath by older code).
  //   - materialPath just became a fresh data: URL (post-generate) while
  //     thumbnailPath is still a saved URL from before this generation.
  const stripCacheBuster = (u?: string) =>
    u ? u.replace(/[?&]v=\d+/g, '').replace(/\?$/, '') : u;
  const resolvePreview = (variant: VariantV3): { src: string; cropZoom: number } | null => {
    const tp = variant.visualization?.thumbnailPath;
    const mp = variant.visualization?.surface?.materialPath;
    const isLegacyThumb = tp && mp && stripCacheBuster(tp) === stripCacheBuster(mp);
    const materialIsFresh = !!mp && mp.startsWith('data:');
    const thumbnailIsStale = !!tp && !tp.startsWith('data:') && materialIsFresh;
    if (tp && !isLegacyThumb && !thumbnailIsStale) {
      return { src: tp, cropZoom: 1 };
    }
    if (mp) {
      const savedZoom = variant.visualization?.surface?.seamlessParams?.zoom;
      return { src: mp, cropZoom: savedZoom && savedZoom > 1 ? savedZoom : 4 };
    }
    return null;
  };

  // Main preview source for the default variant
  const defaultPreview = useMemo(() => {
    if (!defaultVariant) return null;
    return resolvePreview(defaultVariant);
  }, [defaultVariant]);
  const thumbnailUrl = defaultPreview?.src ?? null;

  // Get selected variants with thumbnails for the thumbnail strip
  const selectedVariantsWithThumbnails = useMemo(() => {
    return variants.filter((v) => v.isSelected).map((v) => {
      const preview = resolvePreview(v);
      return {
        ...v,
        thumbnailUrl: preview?.src ?? null,
        cropZoom: preview?.cropZoom ?? 1,
      };
    });
  }, [variants]);

  // Loading state
  if (!product) {
    return (
      <Card title={t('PAGES.NEW_PRODUCT.V3.PREVIEW.TITLE')}>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-[150px] w-full" />
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-4 w-2/5" />
        </div>
      </Card>
    );
  }

  const selectedCount = variants.filter((v) => v.isSelected).length;
  const mainCropZoom = defaultPreview?.cropZoom ?? 1;

  return (
    <Card title={t('PAGES.NEW_PRODUCT.V3.PREVIEW.TITLE')}>
      <div className="flex flex-col gap-4">
        {/* Main Thumbnail */}
        <div className="flex justify-center">
          <div className="flex aspect-square w-full max-w-[180px] items-center justify-center overflow-hidden border bg-muted">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={product.name || 'Product preview'}
                // `cover` + scale gives a centered crop matching the cropper's
                // framing when we have to fall back to the full seamless. For
                // a real cropped thumbnail (cropZoom = 1) it behaves like the
                // original contain.
                className={cn(
                  'size-full origin-center',
                  mainCropZoom > 1 ? 'object-cover' : 'object-contain p-1',
                )}
                style={mainCropZoom > 1 ? { transform: `scale(${mainCropZoom})` } : undefined}
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <ImageIcon className="mx-auto size-9 opacity-40" />
                <p className="mt-1 text-xs">
                  {t('PAGES.NEW_PRODUCT.V3.PREVIEW.NO_PREVIEW')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="text-center">
          {product.name ? (
            <p className="mb-0.5 text-sm font-medium text-foreground">{product.name}</p>
          ) : (
            <Skeleton className="mx-auto mb-0.5 h-4 w-3/5" />
          )}
          {product.brand ? (
            <p className="text-xs text-muted-foreground">{product.brand}</p>
          ) : (
            <Skeleton className="mx-auto h-4 w-2/5" />
          )}
        </div>

        {/* Variant Thumbnails Strip */}
        {selectedVariantsWithThumbnails.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="mb-3 text-center text-xs tracking-wide text-muted-foreground uppercase">
                {t('PAGES.NEW_PRODUCT.V3.PREVIEW.VARIANTS_SELECTED', { selected: selectedCount, total: variants.length })}
              </p>

              {/* Variant Thumbnails Row */}
              <div className="flex flex-wrap justify-center gap-2">
                {selectedVariantsWithThumbnails.map((variant) => (
                  <div
                    key={variant.uuid}
                    className={cn(
                      'flex size-[38px] cursor-pointer items-center justify-center overflow-hidden border bg-muted transition-colors hover:border-foreground/30',
                      variant.isDefault && 'border-[1.5px] border-muted-foreground',
                    )}
                  >
                    {variant.thumbnailUrl ? (
                      <img
                        src={variant.thumbnailUrl}
                        alt={`Variant ${variant.uuid}`}
                        className={cn(
                          'size-full origin-center',
                          variant.cropZoom > 1 ? 'object-cover' : 'object-contain p-0.5',
                        )}
                        style={
                          variant.cropZoom > 1
                            ? { transform: `scale(${variant.cropZoom})` }
                            : undefined
                        }
                      />
                    ) : (
                      <ImageIcon className="size-[18px] text-muted-foreground opacity-50" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Empty state */}
        {variants.length === 0 && (
          <div className="border border-dashed bg-muted p-4 text-center">
            <p className="text-sm text-muted-foreground">
              {t('PAGES.NEW_PRODUCT.V3.PREVIEW.EMPTY_STATE')}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProductPreviewV3;
